import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageCircle, CheckCircle2, XCircle, Loader2, Plus, RefreshCw,
  Phone, ShieldCheck, ExternalLink, Trash2,
} from "lucide-react";

/**
 * WhatsApp Business section: Embedded Signup (Facebook Login for Business)
 * + Meta Template Sync + create template.
 *
 * Loads FB SDK on demand and exchanges the code server-side.
 */
export default function WhatsAppSection() {
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, a] = await Promise.all([
        api.get("/whatsapp/config").then((r) => r.data),
        api.get("/whatsapp/meta-templates").then((r) => r.data).catch(() => ({ data: [] })),
        api.get("/whatsapp/meta-templates/analytics").then((r) => r.data).catch(() => ({ data: [] })),
      ]);
      setCfg(c);
      setTemplates(t.data || []);
      const map = {};
      for (const row of a.data || []) map[row.template_name] = row;
      setAnalytics(map);
    } catch (e) {
      toast.error("Errore caricamento WhatsApp");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* --- Facebook SDK loader --- */
  useEffect(() => {
    if (!cfg?.app_id) return;
    if (window.FB) { setSdkReady(true); return; }
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: cfg.app_id, cookie: true, xfbml: false,
        version: cfg.graph_version || "v22.0",
      });
      setSdkReady(true);
    };
    const s = document.createElement("script");
    s.async = true; s.defer = true; s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    document.body.appendChild(s);
  }, [cfg?.app_id, cfg?.graph_version]);

  /* --- Embedded Signup session coordinator (no 1200ms race) --- */
  const sessionRef = useRef({});
  const submitRef = useRef(false);

  const submitConnect = useCallback(async () => {
    const s = sessionRef.current || {};
    if (submitRef.current || !s.code) return;
    submitRef.current = true;
    setConnecting(true);
    try {
      const res = await api.post("/whatsapp/connect", {
        mode: s.mode || "coexistence", code: s.code,
        waba_id_hint: s.waba_id, phone_number_id_hint: s.phone_number_id,
      });
      toast.success(`Collegato: ${res.data.display_phone_number || res.data.phone_number_id}`);
      await loadAll();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && det.message) || (typeof det === "string" ? det : null) || "Errore durante il collegamento");
    } finally {
      setConnecting(false);
      submitRef.current = false;
      sessionRef.current = {};
    }
  }, [loadAll]);

  useEffect(() => {
    const handler = (evt) => {
      let host = "";
      try { host = new URL(evt.origin).hostname || ""; } catch { return; }
      if (!/facebook\.com$/.test(host)) return;
      let msg = evt.data;
      if (typeof msg === "string") { try { msg = JSON.parse(msg); } catch { return; } }
      if (!msg || msg.type !== "WA_EMBEDDED_SIGNUP") return;
      const ev = msg.event;
      const d = msg.data || {};
      if (ev === "FINISH" || ev === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" || ev === "FINISH_ONLY_WABA") {
        sessionRef.current = { ...sessionRef.current, waba_id: d.waba_id, phone_number_id: d.phone_number_id, finished: true };
        submitConnect();
      } else if (ev === "CANCEL") {
        setConnecting(false);
        toast.message("Collegamento annullato" + (d.current_step ? ` (${d.current_step})` : ""));
      } else if (ev === "ERROR") {
        setConnecting(false);
        toast.error("Meta ha segnalato un errore durante il collegamento");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [submitConnect]);

  const startEmbeddedSignup = (mode = "coexistence") => {
    if (!sdkReady) return toast.error("SDK Facebook ancora in caricamento…");
    if (!cfg?.configuration_id) return toast.error("Configuration ID mancante nel server");
    submitRef.current = false;
    sessionRef.current = { mode };
    setConnecting(true);
    window.FB.login((response) => {
      const code = response?.authResponse?.code;
      if (!code) {
        if (!sessionRef.current.finished) setConnecting(false); // popup chiuso / negato
        return;
      }
      sessionRef.current = { ...sessionRef.current, code };
      // Completa subito se l'evento è già arrivato; altrimenti fallback (il backend
      // può fare discovery server-side del phone number se manca l'hint).
      if (sessionRef.current.finished) submitConnect();
      else setTimeout(submitConnect, 2500);
    }, {
      config_id: cfg.configuration_id,
      response_type: "code",
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: mode === "coexistence" ? "whatsapp_business_app_onboarding" : "",
        sessionInfoVersion: "3",
      },
    });
  };

  const connectPlatform = async () => {
    setConnecting(true);
    try {
      const res = await api.post("/whatsapp/connect", { mode: "platform_test" });
      toast.success(`Collegato (debug): ${res.data.display_phone_number || res.data.phone_number_id}`);
      await loadAll();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && det.message) || (typeof det === "string" ? det : null) || "Errore collegamento numero piattaforma");
    }
    setConnecting(false);
  };

  const disconnect = async () => {
    try {
      await api.post("/whatsapp/disconnect");
      toast.success("WhatsApp scollegato");
      setConfirmDisconnect(false);
      loadAll();
    } catch { toast.error("Errore"); }
  };

  const refreshTemplates = async () => {
    setSyncing(true);
    try {
      const r = await api.get("/whatsapp/meta-templates?refresh=true");
      setTemplates(r.data.data || []);
      toast.success(`Sincronizzati ${r.data.count ?? (r.data.data || []).length} template`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync fallita");
    }
    setSyncing(false);
  };

  if (loading) return (
    <div className="sp-card p-8 flex items-center justify-center text-sm text-[color:var(--sp-text-secondary)]">
      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Carico impostazioni WhatsApp…
    </div>
  );

  return (
    <div data-testid="settings-whatsapp" className="space-y-6">
      {/* --- Connection card --- */}
      <div className="sp-card p-8 space-y-4">
        <div className="flex items-center gap-3">
          <MessageCircle className="h-5 w-5 text-[color:var(--sp-brand)]" />
          <h2 className="font-display text-xl font-black">Numero WhatsApp Business</h2>
        </div>
        <p className="text-sm text-[color:var(--sp-text-secondary)]">
          Collega il tuo numero WhatsApp Business ufficiale (via Meta Embedded Signup) per inviare
          promemoria, promo e ricevere le risposte dei clienti direttamente in Memento.
        </p>

        {cfg?.connected ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-4">
            <div className="h-14 w-14 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
                <CheckCircle2 className="h-3.5 w-3.5" /> WhatsApp Business collegato
              </div>
              <div className="text-base font-bold mt-1">{cfg.connection?.display_phone_number || "—"}</div>
              <div className="text-xs mt-1">{cfg.connection?.verified_name || ""}</div>
              <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                WABA <code>{cfg.connection?.waba_id}</code> · Phone <code>{cfg.connection?.phone_number_id}</code>
              </div>
            </div>
            <button
              onClick={() => setConfirmDisconnect(true)}
              data-testid="whatsapp-disconnect-btn"
              className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5 flex-shrink-0"
            >
              <Trash2 className="h-3.5 w-3.5" /> Scollega
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[color:var(--sp-border)] p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="text-sm text-[color:var(--sp-text-secondary)] mb-4">
              Nessun numero collegato
            </div>
            <button
              onClick={() => startEmbeddedSignup("coexistence")}
              disabled={!sdkReady || connecting}
              data-testid="whatsapp-connect-btn"
              className="sp-btn-primary inline-flex items-center gap-2"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {connecting ? "Collegamento…" : "Collega WhatsApp Business"}
            </button>
            <div className="mt-3 text-xs text-[color:var(--sp-text-muted)] max-w-md mx-auto">
              Collega il numero che utilizzi nella WhatsApp Business App e continua a utilizzarlo
              anche dal telefono.
            </div>
            {cfg?.platform_test_allowed && (
              <div className="mt-4 border-t border-[color:var(--sp-border)] pt-4">
                <button onClick={connectPlatform} disabled={connecting}
                  data-testid="whatsapp-connect-platform-btn"
                  className="text-xs text-[color:var(--sp-text-muted)] underline disabled:opacity-50">
                  [Admin] Usa numero di test piattaforma
                </button>
                <div className="mt-1 text-[11px] text-[color:var(--sp-text-muted)]">
                  Strumento interno di debug — non visibile ai saloni.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- Templates card --- */}
      <div className="sp-card p-8 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-5 w-5 text-[color:var(--sp-brand)]" />
            <h2 className="font-display text-xl font-black">Template Meta approvati</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshTemplates}
              disabled={syncing}
              data-testid="whatsapp-templates-sync-btn"
              className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]"
            >
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Sincronizza da Meta
            </button>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="whatsapp-templates-create-btn"
              className="sp-btn-primary inline-flex items-center gap-1.5 !py-1.5 !px-3 text-sm"
            >
              <Plus className="h-3.5 w-3.5" /> Nuovo template
            </button>
          </div>
        </div>

        <p className="text-sm text-[color:var(--sp-text-secondary)]">
          I template sono i messaggi pre-approvati da Meta che puoi inviare <b>fuori dalla finestra
          24h</b> (es. promemoria, promo compleanno). Solo quelli con status <b>APPROVED</b> sono utilizzabili.
        </p>

        <TemplatesTable templates={templates} analytics={analytics} />
      </div>

      {showCreate && (
        <CreateTemplateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refreshTemplates(); }}
        />
      )}
      {confirmDisconnect && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDisconnect(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="whatsapp-disconnect-dialog">
            <div className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              <h3 className="font-display text-lg font-black">Scollegare WhatsApp?</h3>
            </div>
            <p className="text-sm text-[color:var(--sp-text-secondary)]">
              Verrà rimosso il numero <b>{cfg?.connection?.display_phone_number}</b> ({cfg?.connection?.verified_name}).
            </p>
            <div className="text-xs text-[color:var(--sp-text-muted)] bg-amber-50 border border-amber-200 rounded-lg p-3">
              Non potrai più inviare né ricevere messaggi WhatsApp dai tuoi clienti finché non ricolleghi.
              I messaggi già ricevuti restano salvati.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setConfirmDisconnect(false)}
                className="px-3 py-1.5 text-sm rounded-lg border border-[color:var(--sp-border)]"
                data-testid="whatsapp-disconnect-cancel">
                Annulla
              </button>
              <button onClick={disconnect}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="whatsapp-disconnect-confirm">
                Sì, scollega
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = String(status || "").toUpperCase();
  const map = {
    APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
    PENDING: "bg-amber-100 text-amber-700 border-amber-200",
    IN_APPEAL: "bg-amber-100 text-amber-700 border-amber-200",
    REJECTED: "bg-red-100 text-red-700 border-red-200",
    DISABLED: "bg-neutral-100 text-neutral-600 border-neutral-200",
    PAUSED: "bg-neutral-100 text-neutral-600 border-neutral-200",
  };
  const cls = map[s] || "bg-neutral-100 text-neutral-600 border-neutral-200";
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold border ${cls}`}>{s || "—"}</span>;
}

function TemplatesTable({ templates, analytics = {} }) {
  if (!templates.length) return (
    <div className="text-sm text-[color:var(--sp-text-muted)] italic py-6 text-center">
      Nessun template sincronizzato. Clicca &quot;Sincronizza da Meta&quot; per importare quelli attuali.
    </div>
  );
  return (
    <div className="overflow-x-auto" data-testid="whatsapp-templates-table">
      <table className="w-full text-sm">
        <thead className="text-left border-b border-[color:var(--sp-border)]">
          <tr className="text-xs uppercase tracking-wider text-[color:var(--sp-text-muted)]">
            <th className="py-2 pr-3">Nome</th>
            <th className="py-2 pr-3">Categoria</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Inviati</th>
            <th className="py-2 pr-3">Consegna</th>
            <th className="py-2 pr-3">Letti</th>
            <th className="py-2 pr-3">Risposta</th>
            <th className="py-2">Corpo</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((t) => {
            const body = (t.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
            const a = analytics[t.name] || {};
            return (
              <tr key={t.meta_id || t.name} className="border-b border-[color:var(--sp-border)]/60">
                <td className="py-2 pr-3 font-mono text-xs">{t.name}</td>
                <td className="py-2 pr-3 text-xs">{t.category || "—"}</td>
                <td className="py-2 pr-3"><StatusBadge status={t.status} /></td>
                <td className="py-2 pr-3 tabular-nums">{a.sent ?? 0}</td>
                <td className="py-2 pr-3 tabular-nums"><Rate value={a.delivery_rate} raw={a.delivered} /></td>
                <td className="py-2 pr-3 tabular-nums"><Rate value={a.read_rate} raw={a.read} /></td>
                <td className="py-2 pr-3 tabular-nums"><Rate value={a.reply_rate} raw={a.replied} highlight /></td>
                <td className="py-2 max-w-xs truncate text-[color:var(--sp-text-secondary)]" title={body?.text || ""}>{body?.text || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Rate({ value, raw, highlight }) {
  if (value == null && !raw) return <span className="text-[color:var(--sp-text-muted)]">—</span>;
  const color = highlight
    ? (value >= 30 ? "text-emerald-600" : value >= 10 ? "text-amber-600" : "text-neutral-500")
    : "text-[color:var(--sp-text-secondary)]";
  return (
    <span className={color}>
      <span className="font-medium">{value ?? 0}%</span>
      <span className="text-[10px] text-[color:var(--sp-text-muted)] ml-1">({raw ?? 0})</span>
    </span>
  );
}

function CreateTemplateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", language: "it", category: "UTILITY",
    body: "", footer: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const positions = Array.from(new Set(
      Array.from(form.body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)).map((m) => parseInt(m[1], 10))
    )).sort((a, b) => a - b);
    const body_text = positions.length ? [positions.map((p) => `esempio_${p}`)] : undefined;
    const components = [
      { type: "BODY", text: form.body, ...(body_text ? { example: { body_text } } : {}) },
      ...(form.footer ? [{ type: "FOOTER", text: form.footer }] : []),
    ];
    setSaving(true);
    try {
      await api.post("/whatsapp/meta-templates", {
        name: form.name.toLowerCase().trim(),
        language: form.language,
        category: form.category,
        components,
        allow_category_change: true,
      });
      toast.success("Template inviato per approvazione Meta");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione template");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="whatsapp-template-dialog">
        <h3 className="font-display text-lg font-black">Nuovo template WhatsApp</h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-bold">Nome (lowercase, no spazi)</label>
            <input required data-testid="tpl-name" className="sp-input mt-1" placeholder="es. reminder_appuntamento"
              pattern="[a-z0-9_]+"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold">Lingua</label>
              <select className="sp-input mt-1" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                <option value="it">Italiano (it)</option>
                <option value="en">Inglese (en)</option>
                <option value="en_US">Inglese US (en_US)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold">Categoria</label>
              <select data-testid="tpl-category" className="sp-input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="UTILITY">UTILITY (promemoria, conferme)</option>
                <option value="MARKETING">MARKETING (promo, sconti)</option>
                <option value="AUTHENTICATION">AUTHENTICATION (OTP)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold">Corpo del messaggio</label>
            <textarea required rows={4} data-testid="tpl-body" className="sp-input mt-1" placeholder="Ciao {{1}}, ti ricordiamo il tuo appuntamento del {{2}} alle {{3}}."
              value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">Usa {"{{1}}"}, {"{{2}}"}… per le variabili. Meta compilerà automaticamente esempi.</div>
          </div>
          <div>
            <label className="text-xs font-bold">Footer (opzionale)</label>
            <input className="sp-input mt-1" placeholder="Team Memento" value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-[color:var(--sp-border)]">Annulla</button>
            <button type="submit" disabled={saving} data-testid="tpl-submit-btn" className="sp-btn-primary !py-1.5 !px-3 text-sm inline-flex items-center gap-1.5">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Invia a Meta
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
